import React, { useState, useEffect } from 'react';
import { UserPlus, Trash2, Power, X, Search, Eye, EyeOff, Loader2 } from 'lucide-react';
import axios from 'axios';
import './UserManagement.css';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // --- ROLE FILTER STATE ---
  const [filterRole, setFilterRole] = useState('All'); // 'All', 'Vendor', 'Recruiter'

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'Vendor'
  });

  // --- 1. FETCH USERS ---
  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users`);
      setUsers(res.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]); 
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // --- 2. HANDLERS ---
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/create`, formData);
      setUsers([res.data, ...users]); 
      alert(`${formData.role} created successfully!`);
      setShowModal(false);
      setFormData({ name: '', email: '', password: '', role: 'Vendor' }); 
    } catch (error) {
      alert(error.response?.data?.message || "Failed to create user");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (user) => {
    if(!window.confirm(`Are you sure you want to ${user.status === 'Active' ? 'Deactivate' : 'Activate'} this user?`)) return;

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/status`, {
        id: user._id || user.id,
        role: user.role,
        currentStatus: user.status
      });
      setUsers(users.map(u => u.id === user.id ? { ...u, status: res.data.newStatus } : u));
    } catch (error) {
      alert("Failed to update status");
    }
  };

  const handleDelete = async (user) => {
    if(!window.confirm("Are you sure? This action cannot be undone.")) return;

    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/delete`, {
        id: user._id || user.id,
        role: user.role
      });
      setUsers(users.filter(u => u.id !== user.id));
    } catch (error) {
      alert("Failed to delete user");
    }
  };

  // --- 3. FILTER LOGIC ---
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'All' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  return (
    // MAIN CONTAINER
    <div className="user-mgmt-container" style={{
        display: 'flex', 
        flexDirection: 'column',
        height: 'calc(100vh - 120px)', 
        width: '100%',
        overflow: 'hidden', 
        background: '#fff', 
        borderRadius: '16px', 
        padding: '20px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        border: '1px solid #e2e8f0',
        boxSizing: 'border-box'
    }}>
      
      {/* HEADER SECTION - UPDATED: Removed Title text, aligned button to right */}
      <div className="um-header" style={{
          display:'flex', 
          justifyContent:'flex-end', // Pushes button to the right for a clean look
          alignItems:'center', 
          marginBottom: '20px', 
          flexShrink: 0
      }}>
        {/* Title and Subtitle removed as requested */}
        
        <button className="create-user-btn" onClick={() => setShowModal(true)} style={{
            display:'flex', alignItems:'center', gap:'8px', padding:'10px 16px',
            background:'#2563eb', color:'white', border:'none', borderRadius:'8px',
            fontWeight:'600', cursor:'pointer'
        }}>
          <UserPlus size={18} /> Create New User
        </button>
      </div>

      {/* CONTROLS SECTION */}
      <div className="um-controls" style={{
          display:'flex', justifyContent:'space-between', alignItems:'center', 
          marginBottom:'15px', flexShrink: 0
      }}>
        
        {/* ROLE TABS */}
        <div style={{display:'flex', gap:'5px', background:'#f1f5f9', padding:'4px', borderRadius:'8px'}}>
            {['All', 'Vendor', 'Recruiter'].map(role => (
                <button
                    key={role}
                    onClick={() => setFilterRole(role)}
                    style={{
                        padding:'8px 16px', border:'none', borderRadius:'6px',
                        fontSize:'0.9rem', fontWeight:'600', cursor:'pointer',
                        background: filterRole === role ? '#fff' : 'transparent',
                        color: filterRole === role ? '#2563eb' : '#64748b',
                        boxShadow: filterRole === role ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        transition: 'all 0.2s'
                    }}
                >
                    {role === 'All' ? 'All Users' : role + 's'}
                </button>
            ))}
        </div>

        {/* SEARCH BOX */}
        <div className="search-box" style={{
            display: 'flex', 
            alignItems: 'center', 
            width: '300px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '0 12px',
            background: '#fff'
        }}>
          <Search size={18} color="#94a3b8" style={{flexShrink: 0}} />
          <input 
            type="text" 
            placeholder="Search users..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
                border: 'none',
                outline: 'none',
                width: '100%',
                padding: '10px 0 10px 10px',
                fontSize: '0.9rem',
                color: '#1e293b'
            }}
          />
        </div>
      </div>

      {/* TABLE WRAPPER */}
      <div className="um-table-wrapper" style={{
          flex: 1,           
          overflow: 'auto',  
          border: '1px solid #e2e8f0', 
          borderRadius: '8px',
          position: 'relative'
      }}>
        <table className="um-table" style={{width:'100%', borderCollapse:'collapse'}}>
          <thead style={{position:'sticky', top:0, zIndex:10}}>
            <tr style={{background:'#f8fafc', textAlign:'left'}}>
              <th style={{padding:'16px', borderBottom:'1px solid #e2e8f0', color:'#64748b', fontWeight:'600', fontSize:'0.85rem'}}>Name / ID</th>
              <th style={{padding:'16px', borderBottom:'1px solid #e2e8f0', color:'#64748b', fontWeight:'600', fontSize:'0.85rem'}}>Role</th>
              <th style={{padding:'16px', borderBottom:'1px solid #e2e8f0', color:'#64748b', fontWeight:'600', fontSize:'0.85rem'}}>Email</th>
              <th style={{padding:'16px', borderBottom:'1px solid #e2e8f0', color:'#64748b', fontWeight:'600', fontSize:'0.85rem'}}>Status</th>
              <th style={{padding:'16px', borderBottom:'1px solid #e2e8f0', color:'#64748b', fontWeight:'600', fontSize:'0.85rem'}}>Created At</th>
              <th style={{padding:'16px', borderBottom:'1px solid #e2e8f0', color:'#64748b', fontWeight:'600', fontSize:'0.85rem'}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan="6" style={{padding:'40px', textAlign:'center', color:'#64748b'}}>Loading users...</td></tr>
            ) : filteredUsers.length === 0 ? (
              <tr><td colSpan="6" style={{padding:'40px', textAlign:'center', color:'#64748b'}}>No users found.</td></tr>
            ) : (
              filteredUsers.map(user => (
                <tr key={user.id} style={{borderBottom:'1px solid #f1f5f9'}}>
                  <td style={{padding:'16px'}}>
                    <div className="user-cell" style={{display:'flex', alignItems:'center', gap:'12px'}}>
                      <div style={{
                          width:'36px', height:'36px', borderRadius:'50%', background:'#e2e8f0', 
                          display:'flex', alignItems:'center', justifyContent:'center', fontWeight:'bold', color:'#475569'
                      }}>
                          {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{fontWeight:'600', color:'#1e293b'}}>{user.name}</div>
                        <div style={{fontSize:'0.8rem', color:'#94a3b8'}}>{user.displayId || user.id?.slice(0,8)}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{padding:'16px'}}>
                    <span style={{
                        padding:'4px 10px', borderRadius:'20px', fontSize:'0.85rem', fontWeight:'600',
                        background: user.role === 'Vendor' ? '#f0fdf4' : '#eff6ff',
                        color: user.role === 'Vendor' ? '#16a34a' : '#2563eb',
                        border: `1px solid ${user.role === 'Vendor' ? '#bbf7d0' : '#bfdbfe'}`
                    }}>{user.role}</span>
                  </td>
                  <td style={{padding:'16px', color:'#334155'}}>{user.email}</td>
                  <td style={{padding:'16px'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
                        <span style={{
                            width:'8px', height:'8px', borderRadius:'50%', 
                            background: user.status === 'Active' ? '#22c55e' : '#ef4444'
                        }}></span> 
                        {user.status || 'Active'}
                    </div>
                  </td>
                  <td style={{padding:'16px', color:'#64748b'}}>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '--'}</td>
                  <td style={{padding:'16px'}}>
                    <div style={{display:'flex', gap:'8px'}}>
                      <button 
                        title={user.status === 'Active' ? "Deactivate" : "Activate"}
                        onClick={() => handleToggleStatus(user)}
                        style={{
                            padding:'8px', borderRadius:'6px', border:'none', cursor:'pointer',
                            background: user.status === 'Active' ? '#fee2e2' : '#dcfce7',
                            color: user.status === 'Active' ? '#ef4444' : '#16a34a'
                        }}
                      >
                        <Power size={18} />
                      </button>
                      <button 
                        title="Delete User"
                        onClick={() => handleDelete(user)}
                        style={{
                            padding:'8px', borderRadius:'6px', border:'none', cursor:'pointer',
                            background:'#f1f5f9', color:'#64748b'
                        }}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* CREATE USER MODAL */}
      {showModal && (
        <div className="modal-overlay" style={{
            position:'fixed', top:0, left:0, width:'100%', height:'100%', 
            background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100
        }}>
          <div className="modal-content um-modal" style={{
              background:'#fff', padding:'30px', borderRadius:'16px', width:'450px', maxWidth:'90%'
          }}>
            <div className="modal-header" style={{display:'flex', justifyContent:'space-between', marginBottom:'20px'}}>
              <h3>Create New User</h3>
              <button onClick={() => setShowModal(false)} style={{background:'none', border:'none', cursor:'pointer'}}><X size={20}/></button>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className="form-group" style={{marginBottom:'15px'}}>
                <label style={{display:'block', marginBottom:'5px', fontWeight:'500'}}>Account Type</label>
                <div style={{display:'flex', gap:'10px'}}>
                  <button type="button" 
                    onClick={() => setFormData({...formData, role: 'Vendor'})}
                    style={{
                        flex:1, padding:'10px', borderRadius:'8px', border:'1px solid #e2e8f0', cursor:'pointer',
                        background: formData.role === 'Vendor' ? '#2563eb' : '#fff',
                        color: formData.role === 'Vendor' ? '#fff' : '#1e293b'
                    }}
                  >Vendor</button>
                  <button type="button" 
                    onClick={() => setFormData({...formData, role: 'Recruiter'})}
                    style={{
                        flex:1, padding:'10px', borderRadius:'8px', border:'1px solid #e2e8f0', cursor:'pointer',
                        background: formData.role === 'Recruiter' ? '#2563eb' : '#fff',
                        color: formData.role === 'Recruiter' ? '#fff' : '#1e293b'
                    }}
                  >Recruiter</button>
                </div>
              </div>
              
              <div className="form-group" style={{marginBottom:'15px'}}>
                <label style={{display:'block', marginBottom:'5px', fontWeight:'500'}}>Full Name / Agency Name</label>
                <input 
                  type="text" name="name" required 
                  placeholder={formData.role === 'Vendor' ? "Vendor Name" : "Recruiter/Agency Name"}
                  value={formData.name} onChange={handleInputChange}
                  style={{width:'100%', padding:'10px', borderRadius:'8px', border:'1px solid #e2e8f0'}}
                />
              </div>

              <div className="form-group" style={{marginBottom:'15px'}}>
                <label style={{display:'block', marginBottom:'5px', fontWeight:'500'}}>Email Address</label>
                <input 
                  type="email" name="email" required 
                  placeholder="name@company.com"
                  value={formData.email} onChange={handleInputChange}
                  style={{width:'100%', padding:'10px', borderRadius:'8px', border:'1px solid #e2e8f0'}}
                />
              </div>

              <div className="form-group" style={{marginBottom:'25px'}}>
                <label style={{display:'block', marginBottom:'5px', fontWeight:'500'}}>Password</label>
                <div style={{position:'relative'}}>
                  <input 
                    type={showPassword ? "text" : "password"} 
                    name="password" required 
                    placeholder="Create password"
                    value={formData.password} onChange={handleInputChange}
                    style={{width:'100%', padding:'10px', borderRadius:'8px', border:'1px solid #e2e8f0'}}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    style={{position:'absolute', right:'10px', top:'10px', background:'none', border:'none', cursor:'pointer'}}
                  >
                    {showPassword ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={isSubmitting} style={{
                  width:'100%', padding:'12px', background:'#2563eb', color:'#fff', 
                  border:'none', borderRadius:'8px', fontWeight:'600', cursor:'pointer'
              }}>
                {isSubmitting ? <Loader2 className="animate-spin" size={18}/> : "Create Account"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}